/**
 * 수식 편집기 (spec 2026-09-18-calendar-properties §14): 속성 관리자 행의 textarea 에
 * 자동 완성(속성·함수) · 함수 참고 팝오버 · 입력 400ms 뒤 검사(POST /calendar/formula/check) ·
 * blur/Ctrl+Enter 저장을 붙인다. 식은 실행하지 않고 서버의 판정만 그린다. 문구는 섬의 l.* .
 */
import { completionContext, completions, applyCompletion, runeToIndex } from './calendar-formula-logic.js';
import { openExclusive, closedExclusive } from './popover-registry.js';

let seq = 0;

// 열린 자동 완성·함수 참고의 닫기. Esc 는 창의 캡처 단계에서 먼저 받는다 — 문서 캡처 단계의
// 관리자 닫기·팝오버 레지스트리보다 앞이라, 등록 순서와 무관하게 Esc 한 번이 이 팝오버만 닫는다.
let openFx = null;
if (typeof window !== 'undefined' && !window.__calFormulaEsc) {
    window.__calFormulaEsc = true;
    window.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || !openFx) return;
        const close = openFx;
        openFx = null;
        close();
        e.preventDefault();
        e.stopPropagation();
    }, true);
}

export function bindFormulaEditor(ta, o) {
    const id = 'calpFx' + (++seq);
    const L = (k, fb) => (o.labels && o.labels[k]) || fb;
    const status = o.status;
    status.id = id + 'Status';
    ta.setAttribute('aria-describedby', status.id);
    ta.setAttribute('aria-autocomplete', 'list');
    let timer = 0;
    let turn = 0;
    let lastSaved = ta.value;

    function show(state, text, pos) {
        status.dataset.state = state;
        status.textContent = text;
        status.dataset.pos = pos == null ? '' : String(pos);
        ta.setAttribute('aria-invalid', state === 'error' ? 'true' : 'false');
    }
    const showResult = (type) => show('ok', L('formula_result', '결과') + ': ' + ((o.results || {})[type] || type));
    function showError(body) {
        const pos = body && typeof body.pos === 'number' ? body.pos : null;
        const at = pos == null ? '' : ' · ' + L('formula_at', '{n}번째 글자').replace('{n}', String(pos + 1));
        show('error', ((body && body.message) || L('failed', '저장하지 못했습니다')) + at, pos);
    }
    function check() {
        const mine = ++turn;
        if (!ta.value.trim()) { show('empty', L('formula_empty', '식을 입력하세요')); return; }
        show('checking', L('formula_checking', '확인 중…'));
        o.check(ta.value).then((j) => { if (mine === turn) showResult(j.type); })
            .catch((e) => { if (mine === turn) showError(e && e.body); });
    }
    function save() {
        if (ta.value === lastSaved) return;
        clearTimeout(timer);
        const mine = ++turn; // 저장 결과가 늦게 온 검사 결과를 이긴다
        const sent = ta.value;
        o.save(sent).then((def) => {
            lastSaved = (def && def.expr_display) || sent;
            if (ta.value === sent) ta.value = lastSaved;
            ta.dataset.saved = '1';
            if (mine !== turn) return;
            if (def && def.config && def.config.result) showResult(def.config.result);
            else show('empty', L('formula_empty', '식을 입력하세요'));
        }).catch((e) => { if (mine === turn) showError(e && e.body); });
    }

    // ── 자동 완성 ──
    const ac = document.createElement('ul');
    ac.className = 'calp-fx-ac'; ac.id = id + 'Ac'; ac.setAttribute('role', 'listbox'); ac.hidden = true;
    ac.setAttribute('aria-label', L('formula_expr', '식'));
    ta.setAttribute('aria-controls', ac.id);
    ta.insertAdjacentElement('afterend', ac);
    let items = [];
    let active = 0;
    let acCtx = null;
    function closeAc() {
        ac.hidden = true; items = [];
        ta.removeAttribute('aria-activedescendant');
        ta.setAttribute('aria-expanded', 'false');
        closedExclusive(ac.id);
        if (openFx === closeAc) openFx = null;
    }
    function paintAc() {
        ac.innerHTML = '';
        items.forEach((it, i) => {
            const li = document.createElement('li');
            li.id = ac.id + '-' + i; li.setAttribute('role', 'option'); li.setAttribute('aria-selected', i === active ? 'true' : 'false');
            li.dataset.kind = it.kind;
            const name = document.createElement('span'); name.textContent = it.label; li.appendChild(name);
            if (it.sig) { const s = document.createElement('span'); s.className = 'calp-fx-sig'; s.textContent = it.sig; li.appendChild(s); }
            li.addEventListener('mousedown', (e) => { e.preventDefault(); accept(i); });
            ac.appendChild(li);
        });
        ta.setAttribute('aria-activedescendant', ac.id + '-' + active);
        const cur = ac.children[active];
        if (cur && cur.scrollIntoView) cur.scrollIntoView({ block: 'nearest' });
    }
    function refreshAc() {
        acCtx = completionContext(ta.value, ta.selectionStart);
        items = completions(acCtx, { props: o.props, fns: o.fns });
        if (!items.length) { closeAc(); return; }
        active = 0;
        ac.style.top = (ta.offsetTop + ta.offsetHeight + 2) + 'px';
        ac.style.left = ta.offsetLeft + 'px';
        ac.hidden = false;
        ta.setAttribute('aria-expanded', 'true');
        paintAc();
        openExclusive(ac.id, closeAc, { roots: [ac, ta] });
        openFx = closeAc;
    }
    function accept(i) {
        const out = applyCompletion(ta.value, ta.selectionStart, acCtx, items[i]);
        ta.value = out.text; ta.setSelectionRange(out.caret, out.caret);
        closeAc(); ta.focus();
        ta.dispatchEvent(new Event('input'));
    }

    ta.addEventListener('input', () => {
        delete ta.dataset.saved;
        clearTimeout(timer); timer = setTimeout(check, 400);
        refreshAc();
    });
    ta.addEventListener('keydown', (e) => {
        if (e.isComposing) return; // 한글 조합 중의 Enter·화살표는 IME 의 것
        if (!ac.hidden && items.length) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); active = (active + (e.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length; paintAc(); return; }
            if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); accept(active); return; }
        }
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); save(); }
    });
    ta.addEventListener('blur', () => { if (!ac.hidden) closeAc(); });
    ta.addEventListener('change', save); // blur
    status.addEventListener('click', () => {
        const p = status.dataset.pos;
        if (p === '' || p == null) return;
        const i = runeToIndex(ta.value, Number(p));
        ta.focus(); ta.setSelectionRange(i, Math.min(i + 1, ta.value.length));
    });

    // ── 함수 참고 팝오버 ──
    const ref = o.refButton;
    ref.setAttribute('aria-haspopup', 'dialog');
    ref.setAttribute('aria-expanded', 'false');
    let refPop = null;
    function closeRef(focusBack) {
        if (!refPop) return;
        refPop.remove(); refPop = null;
        ref.setAttribute('aria-expanded', 'false');
        closedExclusive(id + 'Ref');
        if (openFx === closeRefEsc) openFx = null;
        if (focusBack) ref.focus();
    }
    const closeRefEsc = () => closeRef(true);
    ref.addEventListener('click', () => {
        if (refPop) { closeRef(false); return; }
        const pop = document.createElement('div');
        pop.className = 'calp-fx-ref'; pop.id = id + 'Ref'; pop.setAttribute('role', 'dialog'); pop.setAttribute('aria-label', L('formula_fns', '함수'));
        const h = document.createElement('div'); h.className = 'calp-fx-ref-title'; h.textContent = L('formula_ref_title', '함수 — 누르면 커서 자리에 넣습니다');
        pop.appendChild(h);
        (o.fns || []).forEach((f) => {
            const b = document.createElement('button');
            b.type = 'button'; b.className = 'calp-fx-fn'; b.dataset.fn = f.name;
            const code = document.createElement('code'); code.textContent = f.sig;
            const d = document.createElement('span'); d.textContent = f.desc || '';
            b.appendChild(code); b.appendChild(d);
            b.addEventListener('click', () => {
                const at = ta.selectionStart;
                ta.value = ta.value.slice(0, at) + f.name + '(' + ta.value.slice(ta.selectionEnd);
                const c = at + f.name.length + 1;
                closeRef(false); ta.focus(); ta.setSelectionRange(c, c);
                ta.dispatchEvent(new Event('input'));
            });
            pop.appendChild(b);
        });
        if (o.statusKeys) { const n = document.createElement('p'); n.className = 'calp-fx-note'; n.textContent = o.statusKeys; pop.appendChild(n); }
        ref.insertAdjacentElement('afterend', pop);
        refPop = pop;
        ref.setAttribute('aria-expanded', 'true');
        openExclusive(pop.id, () => closeRef(false), { roots: [pop, ref] });
        openFx = closeRefEsc;
        const first = pop.querySelector('.calp-fx-fn'); if (first) first.focus();
    });

    if (o.initial && o.initial.type) showResult(o.initial.type);
    else if (!ta.value.trim()) show('empty', L('formula_empty', '식을 입력하세요'));
    else show('empty', '');
}
